import { useState, useEffect, useRef, useCallback } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Terminal, RefreshCw, Pause, Play, Filter, Clock, User, Activity, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTION_COLORS = {
  created: "text-green-400",
  updated: "text-yellow-400",
  deleted: "text-red-400",
  approved: "text-blue-400",
  denied: "text-orange-400",
  completed: "text-emerald-400",
  default: "text-gray-400"
};

const ENTITY_COLORS = {
  program: "text-purple-400",
  client: "text-cyan-400",
  user: "text-pink-400",
  deliverable: "text-indigo-400",
  milestone: "text-amber-400",
  leave_request: "text-teal-400",
  invitation: "text-rose-400",
  default: "text-gray-500"
};

function formatTimestamp(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit',
    hour12: false 
  });
}

function formatDate(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric'
  });
}

function LogEntry({ log, isNew }) {
  const actionColor = ACTION_COLORS[log.action] || ACTION_COLORS.default;
  const entityColor = ENTITY_COLORS[log.entity_type] || ENTITY_COLORS.default;
  
  return (
    <div className={`font-mono text-sm py-1.5 px-3 border-l-2 ${isNew ? 'border-green-500 bg-green-500/5' : 'border-transparent'} hover:bg-white/5 transition-colors`}>
      <span className="text-gray-500">[{formatTimestamp(log.timestamp)}]</span>
      {" "}
      <span className="text-blue-400">{log.user_name || "System"}</span>
      {" "}
      <span className={actionColor}>{log.action}</span>
      {" "}
      <span className={entityColor}>{log.entity_type}</span>
      {log.details && (
        <>
          {" - "}
          <span className="text-gray-400">{log.details}</span>
        </>
      )}
    </div>
  );
}

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [newLogIds, setNewLogIds] = useState(new Set());
  const scrollRef = useRef(null);
  const lastTimestampRef = useRef(null);

  const fetchLogs = useCallback(async (isInitial = false) => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (!isInitial && lastTimestampRef.current) {
        params.set("since", lastTimestampRef.current);
      }
      
      const res = await apiClient.get(`/activity-logs/stream?${params}`);
      const newLogs = res.data;
      
      if (newLogs.length > 0) {
        if (isInitial) {
          setLogs(newLogs);
          lastTimestampRef.current = newLogs[0]?.timestamp;
        } else {
          // Mark new logs
          const newIds = new Set(newLogs.map(l => l.id));
          setNewLogIds(newIds);
          
          // Merge new logs at the beginning
          setLogs(prev => {
            const existingIds = new Set(prev.map(l => l.id));
            const uniqueNewLogs = newLogs.filter(l => !existingIds.has(l.id));
            return [...uniqueNewLogs, ...prev].slice(0, 500); // Keep max 500
          });
          
          if (newLogs.length > 0) {
            lastTimestampRef.current = newLogs[0]?.timestamp;
          }
          
          // Clear "new" highlight after 5 seconds
          setTimeout(() => setNewLogIds(new Set()), 5000);
        }
      }
    } catch (error) {
      if (isInitial) {
        toast.error("Failed to load activity logs");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(true);
  }, [fetchLogs]);

  // Auto-refresh every 3 seconds when not paused
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      fetchLogs(false);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isPaused, fetchLogs]);

  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (filter !== "all" && log.entity_type !== filter) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        log.user_name?.toLowerCase().includes(searchLower) ||
        log.action?.toLowerCase().includes(searchLower) ||
        log.entity_type?.toLowerCase().includes(searchLower) ||
        log.details?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Group logs by date
  const groupedLogs = filteredLogs.reduce((acc, log) => {
    const date = formatDate(log.timestamp);
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Terminal className="w-6 h-6 text-green-400" />
            <h1 className="text-xl font-mono font-light">Activity Log</h1>
            <Badge 
              variant="outline" 
              className={`font-mono ${isPaused ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' : 'bg-green-500/20 text-green-400 border-green-500/50'}`}
            >
              {isPaused ? "PAUSED" : "LIVE"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPaused(!isPaused)}
              className="font-mono bg-transparent border-gray-700 hover:bg-gray-800"
            >
              {isPaused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLogs(true)}
              className="font-mono bg-transparent border-gray-700 hover:bg-gray-800"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="Search logs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-gray-800 border-gray-700 text-white font-mono"
                  />
                </div>
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px] bg-gray-800 border-gray-700 text-white font-mono">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="program">Programs</SelectItem>
                  <SelectItem value="client">Clients</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                  <SelectItem value="deliverable">Deliverables</SelectItem>
                  <SelectItem value="milestone">Milestones</SelectItem>
                  <SelectItem value="leave_request">Leave Requests</SelectItem>
                  <SelectItem value="invitation">Invitations</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Log Output */}
        <Card className="bg-gray-900 border-gray-800 overflow-hidden">
          <CardHeader className="py-2 px-4 bg-gray-800/50 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-mono text-gray-400">
                  {filteredLogs.length} events
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-mono text-gray-500">
                  Last updated: {lastTimestampRef.current ? formatTimestamp(lastTimestampRef.current) : "-"}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div 
              ref={scrollRef}
              className="h-[calc(100vh-280px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900"
            >
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                  <Terminal className="w-12 h-12 mb-3 opacity-50" />
                  <p className="font-mono">No activity logs found</p>
                </div>
              ) : (
                Object.entries(groupedLogs).map(([date, dateLogs]) => (
                  <div key={date}>
                    <div className="sticky top-0 bg-gray-800/90 backdrop-blur px-3 py-1 border-b border-gray-700">
                      <span className="text-xs font-mono text-gray-400 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {date}
                      </span>
                    </div>
                    {dateLogs.map((log) => (
                      <LogEntry 
                        key={log.id} 
                        log={log} 
                        isNew={newLogIds.has(log.id)}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs font-mono text-gray-600">
          <span>RenderPros OS • Activity Monitor • </span>
          <span className="text-green-600">System Operational</span>
        </div>
      </div>
    </div>
  );
}
